/**
 * CalendarView — Phase G.3 + G.4
 *
 * Owner-only native calendar UI. Lets the team:
 *   - View today's consult goal (1-3/day)
 *   - See upcoming + past appointments with status filtering
 *   - Pick from open slots and schedule a consult for a contact
 *   - Reschedule / cancel existing appointments (24h window enforced)
 *   - Configure recurring weekly availability + blocked dates
 *
 * Hard rule: there is NO public booking surface. Every appointment in
 * this view was scheduled by an owner, ambassador, or the swarm Lead
 * Outreach agent.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarClock,
  CalendarCheck,
  CalendarPlus,
  CalendarX,
  CheckCircle2,
  Clock,
  Download,
  Edit3,
  Loader2,
  Mail,
  MapPin,
  Plus,
  RefreshCw,
  Settings,
  ShieldAlert,
  Sparkles,
  Target,
  Trash2,
  User,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

const WEEKDAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

const STATUS_TONE = {
  scheduled: "border-emerald-500/40 text-emerald-200 bg-emerald-500/5",
  completed: "border-blue-500/40 text-blue-200 bg-blue-500/5",
  cancelled: "border-zinc-700 text-zinc-400 bg-zinc-900/30",
  no_show: "border-amber-500/40 text-amber-200 bg-amber-500/5",
  rescheduled: "border-violet-500/40 text-violet-200 bg-violet-500/5",
};

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "scheduled", label: "Scheduled" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "no_show", label: "No-show" },
];

function formatLocal(iso, opts = {}) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: opts.short ? undefined : "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      ...(opts.year ? { year: "numeric" } : {}),
    });
  } catch {
    return iso;
  }
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function relTime(ts) {
  if (!ts) return "—";
  const dt = new Date(ts);
  const now = new Date();
  const sec = Math.round((dt - now) / 1000);
  const abs = Math.abs(sec);
  if (abs < 60) return sec >= 0 ? "in a moment" : "just now";
  if (abs < 3600) {
    const m = Math.round(abs / 60);
    return sec >= 0 ? `in ${m}m` : `${m}m ago`;
  }
  if (abs < 86400) {
    const h = Math.round(abs / 3600);
    return sec >= 0 ? `in ${h}h` : `${h}h ago`;
  }
  const d = Math.round(abs / 86400);
  return sec >= 0 ? `in ${d}d` : `${d}d ago`;
}

function StatusBadge({ status }) {
  return (
    <Badge
      variant="outline"
      className={`text-[10px] py-0 ${STATUS_TONE[status] || "border-zinc-700 text-zinc-400"}`}
      data-testid={`appt-status-${status}`}
    >
      {status?.replace(/_/g, " ") || "—"}
    </Badge>
  );
}

function Kpi({ icon: Icon, label, value, hint, tone = "default", testId }) {
  const toneCls =
    tone === "pass"
      ? "text-emerald-300"
      : tone === "warn"
      ? "text-amber-300"
      : tone === "fail"
      ? "text-red-300"
      : "text-white";
  return (
    <Card className="bg-zinc-950 border-zinc-800" data-testid={testId}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-zinc-500">
          <Icon className="h-3 w-3" />
          {label}
        </div>
        <div className={`text-2xl font-semibold mt-1 ${toneCls}`}>{value}</div>
        {hint ? <div className="text-[10.5px] text-zinc-500 mt-0.5">{hint}</div> : null}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// New Appointment Dialog (exported so ProspectsView + others can reuse it)
// ---------------------------------------------------------------------------

export function NewAppointmentDialog({
  open,
  onOpenChange,
  onCreated,
  initialContact = null,
  prospectId = null,
}) {
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    address: "",
    title: "Roof / property walk-through",
    notes: "",
    selected_slot: "",
  });

  const loadSlots = useCallback(async () => {
    setLoadingSlots(true);
    try {
      const today = new Date();
      const horizon = new Date();
      horizon.setDate(today.getDate() + 14);
      const toDateStr = (d) => d.toISOString().slice(0, 10);
      const res = await crmApi.calendarSlots({
        from_date: toDateStr(today),
        to_date: toDateStr(horizon),
      });
      setSlots(res?.slots || []);
    } catch (err) {
      toast.error(err.message || "Failed to load slots");
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadSlots();
      setForm({
        contact_name: initialContact?.contact_name || "",
        contact_email: initialContact?.contact_email || "",
        contact_phone: initialContact?.contact_phone || "",
        address: initialContact?.address || "",
        title: initialContact?.title || "Roof / property walk-through",
        notes: initialContact?.notes || "",
        selected_slot: "",
      });
    }
  }, [open, loadSlots, initialContact]);

  const slotsByDay = useMemo(() => {
    const map = {};
    for (const s of slots) {
      const dayKey = new Date(s.start_ts).toISOString().slice(0, 10);
      if (!map[dayKey]) map[dayKey] = [];
      map[dayKey].push(s);
    }
    return map;
  }, [slots]);

  const sortedDays = useMemo(() => Object.keys(slotsByDay).sort(), [slotsByDay]);

  async function handleSubmit() {
    if (!form.contact_name.trim() || !form.contact_email.trim()) {
      toast.error("Contact name and email are required");
      return;
    }
    if (!form.selected_slot) {
      toast.error("Please pick a time slot");
      return;
    }
    setSubmitting(true);
    try {
      await crmApi.calendarCreateAppointment({
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim() || undefined,
        address: form.address.trim() || undefined,
        title: form.title.trim() || undefined,
        notes: form.notes.trim() || undefined,
        start_ts: form.selected_slot,
        prospect_id: prospectId || undefined,
        send_confirmation: true,
      });
      toast.success("Appointment scheduled \u2014 confirmation email sent");
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      const code = err?.body?.code || err?.code || "error";
      const msg = err?.body?.message || err.message || "Failed to schedule";
      toast.error(`${code}: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto bg-zinc-950 border-zinc-800 text-white"
        data-testid="new-appointment-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-emerald-300" />
            Schedule a consult
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Internal-only. The prospect gets an email confirmation with a
            calendar invite attached.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
          <div>
            <Label className="text-zinc-300 text-xs">Contact name *</Label>
            <Input
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              className="bg-black border-zinc-800 text-white"
              placeholder="Jane Smith"
              data-testid="new-appt-contact-name"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Contact email *</Label>
            <Input
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="bg-black border-zinc-800 text-white"
              placeholder="jane@example.com"
              type="email"
              data-testid="new-appt-contact-email"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Contact phone</Label>
            <Input
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              className="bg-black border-zinc-800 text-white"
              placeholder="(optional)"
              data-testid="new-appt-contact-phone"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Property address</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="bg-black border-zinc-800 text-white"
              placeholder="123 Main St, Austin TX"
              data-testid="new-appt-address"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-300 text-xs">Consult title</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-black border-zinc-800 text-white"
              data-testid="new-appt-title"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-zinc-300 text-xs">Internal notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="bg-black border-zinc-800 text-white h-20"
              placeholder="Lead source, special instructions..."
              data-testid="new-appt-notes"
            />
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-zinc-300 text-xs uppercase tracking-[0.14em]">
              Available slots (next 14 days)
            </Label>
            <Button
              size="sm"
              variant="ghost"
              onClick={loadSlots}
              disabled={loadingSlots}
              className="h-7 px-2 text-zinc-400 hover:text-white"
              data-testid="new-appt-refresh-slots"
            >
              {loadingSlots ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
          {loadingSlots ? (
            <div className="text-zinc-500 text-sm py-4">Loading slots…</div>
          ) : sortedDays.length === 0 ? (
            <div className="text-zinc-500 text-sm py-4">
              No open slots in the next 14 days. Adjust your availability in the Calendar settings.
            </div>
          ) : (
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {sortedDays.map((day) => (
                <div key={day}>
                  <div className="text-[10.5px] uppercase tracking-[0.14em] text-zinc-500 mb-1">
                    {formatDate(day + "T12:00:00Z")}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {slotsByDay[day].map((slot) => {
                      const isSelected = form.selected_slot === slot.start_ts;
                      return (
                        <button
                          key={slot.start_ts}
                          type="button"
                          onClick={() =>
                            setForm({ ...form, selected_slot: slot.start_ts })
                          }
                          data-testid={`slot-${slot.start_ts}`}
                          className={`px-2.5 py-1.5 text-[11.5px] rounded border transition-colors ${
                            isSelected
                              ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                              : "border-zinc-800 bg-black text-zinc-300 hover:border-zinc-600 hover:text-white"
                          }`}
                        >
                          {formatTime(slot.start_ts)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            data-testid="new-appt-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !form.selected_slot}
            className="bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-50"
            data-testid="new-appt-submit"
          >
            {submitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            ) : (
              <CalendarCheck className="h-3.5 w-3.5 mr-1.5" />
            )}
            Schedule + email invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Appointment Detail / Cancel Dialog
// ---------------------------------------------------------------------------

function AppointmentDetailDialog({ appointment, open, onOpenChange, onChanged }) {
  const [cancelReason, setCancelReason] = useState("");
  const [overrideWindow, setOverrideWindow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setCancelReason("");
      setOverrideWindow(false);
    }
  }, [open]);

  if (!appointment) return null;

  async function handleCancel() {
    setBusy(true);
    try {
      await crmApi.calendarCancelAppointment(appointment.id, {
        reason: cancelReason || "",
        override_window: overrideWindow,
        send_cancel_email: true,
      });
      toast.success("Appointment cancelled");
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      const code = err?.body?.code || "error";
      const msg = err?.body?.message || err.message || "Cancel failed";
      toast.error(`${code}: ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  async function markCompleted() {
    setBusy(true);
    try {
      await crmApi.calendarUpdateAppointment(appointment.id, {
        status: "completed",
        send_update_email: false,
      });
      toast.success("Marked completed");
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Update failed");
    } finally {
      setBusy(false);
    }
  }

  async function markNoShow() {
    setBusy(true);
    try {
      await crmApi.calendarUpdateAppointment(appointment.id, {
        status: "no_show",
        send_update_email: false,
      });
      toast.success("Marked no-show");
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Update failed");
    } finally {
      setBusy(false);
    }
  }

  const canCancel = appointment.status === "scheduled";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl bg-zinc-950 border-zinc-800 text-white"
        data-testid="appt-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-emerald-300" />
            {appointment.title || "Consult"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {formatLocal(appointment.start_ts)} · {appointment.duration_minutes} min
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12.5px] text-zinc-300 mt-1">
          <div className="flex items-start gap-1.5">
            <User className="h-3.5 w-3.5 text-zinc-500 mt-0.5" />
            <span data-testid="appt-detail-contact">{appointment.contact_name}</span>
          </div>
          <div className="flex items-start gap-1.5">
            <Mail className="h-3.5 w-3.5 text-zinc-500 mt-0.5" />
            <span className="break-all">{appointment.contact_email}</span>
          </div>
          {appointment.contact_phone ? (
            <div className="flex items-start gap-1.5">
              <CalendarClock className="h-3.5 w-3.5 text-zinc-500 mt-0.5" />
              <span>{appointment.contact_phone}</span>
            </div>
          ) : null}
          {appointment.location || appointment.address ? (
            <div className="flex items-start gap-1.5 sm:col-span-2">
              <MapPin className="h-3.5 w-3.5 text-zinc-500 mt-0.5" />
              <span>{appointment.location || appointment.address}</span>
            </div>
          ) : null}
        </div>

        {appointment.description ? (
          <div className="mt-3 text-[12px] text-zinc-400 border-t border-zinc-800 pt-2">
            {appointment.description}
          </div>
        ) : null}
        {appointment.notes ? (
          <div className="mt-2 text-[12px] text-amber-200 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1.5">
            <span className="text-amber-400/80 text-[10px] uppercase tracking-[0.14em] block mb-1">
              Internal notes
            </span>
            {appointment.notes}
          </div>
        ) : null}

        <div className="text-[10.5px] text-zinc-500 mt-3 flex flex-wrap gap-x-3 gap-y-1">
          <span>
            scheduled by <span className="text-zinc-300">{appointment.scheduled_by_label || appointment.scheduled_by}</span>
          </span>
          <span>
            <StatusBadge status={appointment.status} />
          </span>
          {appointment.ics_uid ? (
            <a
              href={crmApi.calendarIcsUrl(appointment.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-300 hover:underline inline-flex items-center gap-1"
              data-testid="appt-detail-download-ics"
            >
              <Download className="h-3 w-3" /> ICS
            </a>
          ) : null}
        </div>

        {canCancel ? (
          <div className="mt-4 border-t border-zinc-800 pt-3">
            <Label className="text-zinc-300 text-xs">Cancel reason (optional)</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              className="bg-black border-zinc-800 text-white h-16 mt-1"
              placeholder="Prospect rescheduled / no longer interested..."
              data-testid="appt-cancel-reason"
            />
            <div className="flex items-center justify-between mt-2 text-[11.5px]">
              <div className="flex items-center gap-2">
                <Switch
                  checked={overrideWindow}
                  onCheckedChange={setOverrideWindow}
                  data-testid="appt-cancel-override-switch"
                />
                <span className="text-zinc-400 flex items-center gap-1">
                  <ShieldAlert className="h-3 w-3 text-amber-400" />
                  Override 24h window
                </span>
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter className="mt-3 flex flex-wrap gap-2">
          {canCancel ? (
            <>
              <Button
                variant="outline"
                onClick={markNoShow}
                disabled={busy}
                className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                data-testid="appt-mark-no-show"
              >
                Mark no-show
              </Button>
              <Button
                variant="outline"
                onClick={markCompleted}
                disabled={busy}
                className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
                data-testid="appt-mark-completed"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                Mark completed
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={busy}
                className="border-red-500/40 text-red-200 hover:bg-red-500/10"
                data-testid="appt-cancel-confirm"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CalendarX className="h-3.5 w-3.5 mr-1.5" />}
                Cancel appointment
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Availability Settings Dialog
// ---------------------------------------------------------------------------

function AvailabilitySettingsDialog({ open, onOpenChange, availability, onSaved }) {
  const [draft, setDraft] = useState(availability || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(JSON.parse(JSON.stringify(availability || {})));
    }
  }, [open, availability]);

  if (!draft) return null;

  function updateHours(dayKey, idx, side, value) {
    const next = { ...draft };
    const ranges = [...(next.work_hours?.[dayKey] || [])];
    const range = [...(ranges[idx] || ["", ""])];
    range[side === "start" ? 0 : 1] = value;
    ranges[idx] = range;
    next.work_hours = { ...(next.work_hours || {}), [dayKey]: ranges };
    setDraft(next);
  }

  function addWindow(dayKey) {
    const next = { ...draft };
    const ranges = [...(next.work_hours?.[dayKey] || [])];
    ranges.push(["09:00", "12:00"]);
    next.work_hours = { ...(next.work_hours || {}), [dayKey]: ranges };
    setDraft(next);
  }

  function removeWindow(dayKey, idx) {
    const next = { ...draft };
    const ranges = [...(next.work_hours?.[dayKey] || [])];
    ranges.splice(idx, 1);
    next.work_hours = { ...(next.work_hours || {}), [dayKey]: ranges };
    setDraft(next);
  }

  async function save() {
    setSaving(true);
    try {
      await crmApi.calendarUpdateAvailability({
        slot_duration_minutes: Number(draft.slot_duration_minutes) || 60,
        buffer_minutes: Number(draft.buffer_minutes) || 0,
        advance_booking_min_hours: Number(draft.advance_booking_min_hours) || 0,
        advance_booking_max_days: Number(draft.advance_booking_max_days) || 30,
        cancellation_window_hours: Number(draft.cancellation_window_hours) || 24,
        daily_consult_goal_min: Number(draft.daily_consult_goal_min) || 1,
        daily_consult_goal_max: Number(draft.daily_consult_goal_max) || 3,
        work_hours: draft.work_hours,
        blocked_dates: draft.blocked_dates || [],
        notes: draft.notes || "",
      });
      toast.success("Availability saved");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      const code = err?.body?.code || "error";
      const msg = err?.body?.message || err.message || "Save failed";
      toast.error(`${code}: ${msg}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto bg-zinc-950 border-zinc-800 text-white"
        data-testid="availability-settings-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Settings className="h-5 w-5 text-emerald-300" />
            Calendar settings
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Configure weekly availability, consult length, and the 1–3 daily goal.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
          <div>
            <Label className="text-zinc-300 text-xs">Slot length (min)</Label>
            <Input
              type="number"
              value={draft.slot_duration_minutes ?? 60}
              onChange={(e) => setDraft({ ...draft, slot_duration_minutes: e.target.value })}
              className="bg-black border-zinc-800 text-white"
              data-testid="settings-slot-duration"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Buffer (min)</Label>
            <Input
              type="number"
              value={draft.buffer_minutes ?? 15}
              onChange={(e) => setDraft({ ...draft, buffer_minutes: e.target.value })}
              className="bg-black border-zinc-800 text-white"
              data-testid="settings-buffer"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Min lead time (h)</Label>
            <Input
              type="number"
              value={draft.advance_booking_min_hours ?? 4}
              onChange={(e) => setDraft({ ...draft, advance_booking_min_hours: e.target.value })}
              className="bg-black border-zinc-800 text-white"
              data-testid="settings-min-lead"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Cancel window (h)</Label>
            <Input
              type="number"
              value={draft.cancellation_window_hours ?? 24}
              onChange={(e) => setDraft({ ...draft, cancellation_window_hours: e.target.value })}
              className="bg-black border-zinc-800 text-white"
              data-testid="settings-cancel-window"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Daily goal min</Label>
            <Input
              type="number"
              value={draft.daily_consult_goal_min ?? 1}
              onChange={(e) => setDraft({ ...draft, daily_consult_goal_min: e.target.value })}
              className="bg-black border-zinc-800 text-white"
              data-testid="settings-goal-min"
            />
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">Daily goal max</Label>
            <Input
              type="number"
              value={draft.daily_consult_goal_max ?? 3}
              onChange={(e) => setDraft({ ...draft, daily_consult_goal_max: e.target.value })}
              className="bg-black border-zinc-800 text-white"
              data-testid="settings-goal-max"
            />
          </div>
          <div className="col-span-2">
            <Label className="text-zinc-300 text-xs">Timezone</Label>
            <Input
              value={draft.timezone || "America/Chicago"}
              disabled
              className="bg-black border-zinc-800 text-zinc-500"
            />
          </div>
        </div>

        <div className="mt-5">
          <Label className="text-zinc-300 text-xs uppercase tracking-[0.14em]">
            Weekly availability
          </Label>
          <div className="space-y-1.5 mt-2">
            {WEEKDAYS.map((day) => {
              const ranges = draft.work_hours?.[day.key] || [];
              return (
                <div key={day.key} className="flex flex-wrap items-center gap-2 border border-zinc-800 rounded p-2 bg-black">
                  <div className="text-[11.5px] uppercase tracking-[0.14em] text-zinc-400 w-20">
                    {day.label}
                  </div>
                  {ranges.length === 0 ? (
                    <span className="text-zinc-600 text-[11.5px]">Closed</span>
                  ) : null}
                  {ranges.map((range, idx) => (
                    <div key={idx} className="flex items-center gap-1">
                      <Input
                        type="time"
                        value={range[0] || ""}
                        onChange={(e) => updateHours(day.key, idx, "start", e.target.value)}
                        className="bg-zinc-950 border-zinc-800 text-white h-7 px-2 w-24 text-[12px]"
                        data-testid={`hours-${day.key}-${idx}-start`}
                      />
                      <span className="text-zinc-500 text-[11px]">to</span>
                      <Input
                        type="time"
                        value={range[1] || ""}
                        onChange={(e) => updateHours(day.key, idx, "end", e.target.value)}
                        className="bg-zinc-950 border-zinc-800 text-white h-7 px-2 w-24 text-[12px]"
                        data-testid={`hours-${day.key}-${idx}-end`}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeWindow(day.key, idx)}
                        className="h-7 w-7 p-0 text-zinc-500 hover:text-red-300"
                        data-testid={`hours-${day.key}-${idx}-remove`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => addWindow(day.key)}
                    className="h-7 px-2 text-emerald-300 hover:bg-emerald-500/10"
                    data-testid={`hours-${day.key}-add`}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Window
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-3">
          <Label className="text-zinc-300 text-xs">Notes (for internal reference)</Label>
          <Textarea
            value={draft.notes || ""}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            className="bg-black border-zinc-800 text-white h-16"
            data-testid="settings-notes"
          />
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-zinc-800 text-zinc-300 hover:bg-zinc-900"
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-emerald-500 text-black hover:bg-emerald-400"
            data-testid="availability-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
            Save settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main View
// ---------------------------------------------------------------------------

export default function CalendarView() {
  const [availability, setAvailability] = useState(null);
  const [progress, setProgress] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("scheduled");
  const [newOpen, setNewOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const [av, prog, list] = await Promise.all([
        crmApi.calendarAvailability(),
        crmApi.calendarToday(),
        crmApi.calendarAppointments({ ...params, limit: 200 }),
      ]);
      setAvailability(av);
      setProgress(prog);
      setAppointments(list?.items || []);
    } catch (err) {
      toast.error(err.message || "Failed to load calendar");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const upcoming = useMemo(() => {
    const nowIso = new Date().toISOString();
    return appointments.filter((a) => a.start_ts >= nowIso);
  }, [appointments]);

  const past = useMemo(() => {
    const nowIso = new Date().toISOString();
    return appointments.filter((a) => a.start_ts < nowIso);
  }, [appointments]);

  return (
    <div className="space-y-4" data-testid="calendar-view">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-emerald-300" />
            Calendar
          </h1>
          <p className="text-[11.5px] text-zinc-500 mt-0.5">
            Owner / ambassador / swarm-scheduled consults only. No public self-booking.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSettingsOpen(true)}
            className="border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            data-testid="calendar-settings-btn"
          >
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            Availability
          </Button>
          <Button
            size="sm"
            onClick={() => setNewOpen(true)}
            className="bg-emerald-500 text-black hover:bg-emerald-400"
            data-testid="calendar-new-appt-btn"
          >
            <CalendarPlus className="h-3.5 w-3.5 mr-1.5" />
            Schedule consult
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={loadAll}
            className="text-zinc-400 hover:text-white"
            data-testid="calendar-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi
          icon={Target}
          label="Today's progress"
          value={progress ? `${progress.scheduled + progress.completed} / ${progress.target_min}-${progress.target_max}` : "—"}
          hint={progress ? (progress.on_track ? "on track" : `goal: ${progress.target_min}-${progress.target_max}/day`) : "—"}
          tone={progress?.on_track ? "pass" : "warn"}
          testId="calendar-kpi-today"
        />
        <Kpi
          icon={CheckCircle2}
          label="Completed today"
          value={progress?.completed ?? 0}
          hint="finished consults"
          tone={progress?.completed > 0 ? "pass" : "default"}
          testId="calendar-kpi-completed"
        />
        <Kpi
          icon={CalendarClock}
          label="Upcoming"
          value={upcoming.filter((a) => a.status === "scheduled").length}
          hint={`next ${upcoming[0] ? relTime(upcoming[0].start_ts) : "\u2014"}`}
          testId="calendar-kpi-upcoming"
        />
        <Kpi
          icon={XCircle}
          label="Cancelled today"
          value={progress?.cancelled ?? 0}
          hint={progress?.no_show ? `${progress.no_show} no-show` : "—"}
          tone={progress?.cancelled > 0 ? "warn" : "default"}
          testId="calendar-kpi-cancelled"
        />
      </div>

      {/* Filter + list */}
      <Card className="bg-zinc-950 border-zinc-800">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full sm:w-auto">
              <TabsList className="bg-black border border-zinc-800">
                {STATUS_OPTIONS.map((o) => (
                  <TabsTrigger
                    key={o.value}
                    value={o.value}
                    className="text-[11.5px] data-[state=active]:bg-zinc-900 data-[state=active]:text-white"
                    data-testid={`calendar-filter-${o.value}`}
                  >
                    {o.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <span className="text-[10.5px] text-zinc-500" data-testid="calendar-list-count">
              {appointments.length} appointment{appointments.length === 1 ? "" : "s"}
            </span>
          </div>

          {loading ? (
            <div className="py-8 text-center text-zinc-500 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : appointments.length === 0 ? (
            <div className="py-10 text-center text-zinc-500">
              <CalendarClock className="h-6 w-6 mx-auto mb-2 text-zinc-700" />
              <div className="text-[13px] text-zinc-400">No appointments yet</div>
              <div className="text-[11.5px] mt-1">
                Click <span className="text-white">Schedule consult</span> to book one for a prospect.
              </div>
            </div>
          ) : (
            <div className="divide-y divide-zinc-900" data-testid="calendar-appt-list">
              {appointments.map((appt) => {
                const nowIso = new Date().toISOString();
                const isPast = appt.start_ts < nowIso;
                return (
                  <button
                    key={appt.id}
                    type="button"
                    onClick={() => setSelectedAppt(appt)}
                    className="w-full text-left py-2.5 px-1 hover:bg-zinc-900/40 rounded transition-colors flex items-start justify-between gap-3"
                    data-testid={`appt-row-${appt.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-[12.5px] font-semibold ${isPast ? "text-zinc-400" : "text-white"}`}>
                          {appt.contact_name}
                        </span>
                        <StatusBadge status={appt.status} />
                      </div>
                      <div className="text-[11.5px] text-zinc-400 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                        <span className="text-zinc-300">{formatLocal(appt.start_ts)}</span>
                        <span className="text-zinc-600">·</span>
                        <span>{appt.duration_minutes}m</span>
                        <span className="text-zinc-600">·</span>
                        <span className="break-all text-zinc-500">{appt.contact_email}</span>
                        {appt.address ? (
                          <>
                            <span className="text-zinc-600">·</span>
                            <span className="text-zinc-500 truncate">{appt.address}</span>
                          </>
                        ) : null}
                      </div>
                      {appt.notes ? (
                        <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                          {appt.notes}
                        </div>
                      ) : null}
                    </div>
                    <div className="text-[10.5px] text-zinc-500 whitespace-nowrap">
                      {relTime(appt.start_ts)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <NewAppointmentDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={loadAll}
      />
      <AppointmentDetailDialog
        appointment={selectedAppt}
        open={!!selectedAppt}
        onOpenChange={(o) => { if (!o) setSelectedAppt(null); }}
        onChanged={loadAll}
      />
      <AvailabilitySettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        availability={availability}
        onSaved={loadAll}
      />
    </div>
  );
}
